import React, { useState } from 'react';
import { Building2, Loader2, Plus, Wifi, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { transportApi } from '@/services/transportApi';

/** Transport Settings: office internet IPs → attendance punches from them show the office name. */
export const OfficeNetworks = ({ value, onChange, description }) => {
  const [ip, setIp] = useState('');
  const [name, setName] = useState('');
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);

  const useMine = async () => {
    setBusy(true);
    try {
      const r = await transportApi.myIp();
      if (!r.ip) { toast.error('Could not detect your IP'); return; }
      setIp(r.ip); setHint(r.network ? `Your network: ${r.network}` : '');
    } catch { toast.error('Could not detect your IP'); } finally { setBusy(false); }
  };
  const add = () => {
    const v = ip.trim();
    if (!v) { toast.error('Enter an IP address'); return; }
    if (value.some((o) => o.ip === v)) { toast.error('That IP is already saved'); return; }
    onChange([...value, { ip: v, name: name.trim() || 'Office Wi-Fi' }]);
    setIp(''); setName(''); setHint('');
  };

  return (
    <Card className="border-0 shadow-md"><CardContent className="p-5">
      <h3 className="font-semibold flex items-center gap-2"><Wifi className="w-4 h-4 text-emerald-600" />Office Wi-Fi (attendance)</h3>
      <p className="text-sm text-gray-500 mb-3">{description || <>Driver Check In / Check Out from these internet IPs shows the office name instead of the provider — old punches too. Tip: connect to the office Wi-Fi and tap <b>Use my current IP</b>. A range like 49.37.10.0/24 also works.</>}</p>
      <div className="space-y-2 mb-3" data-testid="settings-office-list">
        {value.length === 0 && <p className="text-xs text-gray-400" data-testid="settings-office-empty">No office IPs saved yet.</p>}
        {value.map((o) => (
          <div key={o.ip} className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-100 rounded-md px-3 py-1.5" data-testid={`settings-office-${o.ip}`}>
            <Building2 className="w-3.5 h-3.5 text-emerald-600" /><span className="font-medium">{o.name}</span><span className="text-gray-500 font-mono text-xs">{o.ip}</span>
            <button onClick={() => onChange(value.filter((x) => x.ip !== o.ip))} className="ml-auto text-gray-400 hover:text-red-500" data-testid={`settings-office-remove-${o.ip}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="IP address, e.g. 49.37.10.25" className="max-w-[220px] font-mono" data-testid="settings-office-ip-input" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Head Office Wi-Fi" className="max-w-[220px]" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="settings-office-name-input" />
        <Button variant="outline" onClick={useMine} disabled={busy} data-testid="settings-office-myip-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wifi className="w-4 h-4 mr-1" />}Use my current IP</Button>
        <Button variant="outline" onClick={add} data-testid="settings-office-add-btn"><Plus className="w-4 h-4" /></Button>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1" data-testid="settings-office-hint">{hint}</p>}
      <p className="text-xs text-gray-400 mt-2">Press <b>Save Settings</b> below to keep changes.</p>
    </CardContent></Card>
  );
};

export default OfficeNetworks;
